import { Box } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useMemo, useRef, useEffect } from "react"
import { Timeline, timelineItemClasses } from "@mui/lab"
import { RoadmapImplementationType, RoadmapItem, RoadmapItemType } from "../newsTypes"
import { RoadmapFeatureCard } from "./RoadmapFeatureCard"
import { RoadmapTimelineItem } from "./RoadmapTimelineItem"
import { useActiveScrollItem } from "../useActiveScrollItem"
import { StandardNoResultText } from "../../../shared/components/noResult/StandardNoResultText"
import { StandardTabs } from "../../../shared/components/tabs/StandardTabs"
import { useAppLocalization } from "../../localization/components/AppLocalizationProvider"
import { TranslationKey } from "../../localization/translations"

const ROADMAP_FILTER_QUERY_PARAM = "roadmapFilter"

const useListStyles = makeStyles()(() => ({
    layout: {
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gap: "1.5rem",
        alignItems: "start",
    },
    mainContent: {
        order: 1,
        overflowY: "auto",
        paddingBottom: "35rem",
        height: "100%",
    },
    timeline: {
        order: 2,
    },
    timelineContainer: {
        position: "sticky",
        top: "4rem",
        order: 2,
        maxHeight: "calc(100vh - 5rem)",
        overflowY: "auto",
    },
}))

const useStyles = makeStyles()(() => ({
    root: {
        padding: "0 1rem",
    },
}))

type RoadmapListProps = Readonly<{
    items: RoadmapItem[]
}>

function RoadmapList({ items }: RoadmapListProps) {
    const { classes } = useListStyles()
    const featureRefs = useRef<Map<string, HTMLDivElement>>(new Map())
    const timelineItemRefs = useRef<Map<string, HTMLLIElement>>(new Map())
    const timelineContainerRef = useRef<HTMLDivElement>(null)

    const { activeItemId, setActiveItemId } = useActiveScrollItem(featureRefs, items)

    useEffect(() => {
        if (items.length > 0 && !activeItemId) {
            setActiveItemId(items[0].permaLink)
        }
    }, [items, activeItemId])

    // When the active item changes (driven by left-column scroll), scroll the
    // timeline panel to keep the highlighted item in view.
    // Uses manual container scroll instead of scrollIntoView to avoid bubbling to window.
    // Debounced to avoid glitching while the main scroll is still in motion.
    useEffect(() => {
        if (!activeItemId) return

        const timer = setTimeout(() => {
            const container = timelineContainerRef.current
            const el = timelineItemRefs.current.get(activeItemId)
            if (!container || !el) return

            const containerRect = container.getBoundingClientRect()
            const elRect = el.getBoundingClientRect()

            const isAbove = elRect.top < containerRect.top
            const isBelow = elRect.bottom > containerRect.bottom

            if (isAbove || isBelow) {
                const targetScrollTop =
                    container.scrollTop + elRect.top - containerRect.top - containerRect.height / 2 + elRect.height / 2
                container.scrollTo({ top: targetScrollTop, behavior: "smooth" })
            }
        }, 120)

        return () => clearTimeout(timer)
    }, [activeItemId])

    const handleTimelineClick = (permaLink: string) => {
        const element = featureRefs.current.get(permaLink)
        if (element) {
            const HEADER_OFFSET = 80
            const top = element.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET
            window.scrollTo({ top, behavior: "smooth" })
        }
    }

    if (items.length === 0) return <StandardNoResultText />

    return (
        <Box className={classes.layout}>
            <Box className={classes.mainContent}>
                {items.map((item) => (
                    <RoadmapFeatureCard
                        key={item.permaLink}
                        item={item}
                        ref={(el) => {
                            if (el) featureRefs.current.set(item.permaLink, el)
                            else featureRefs.current.delete(item.permaLink)
                        }}
                    />
                ))}
            </Box>

            <Box className={classes.timelineContainer} ref={timelineContainerRef}>
                <Timeline
                    className={classes.timeline}
                    position="right"
                    sx={{
                        position: "relative",
                        top: "-1rem",
                        [`& .${timelineItemClasses.root}:before`]: {
                            flex: 0,
                            padding: 0,
                        },
                    }}
                >
                    {items.map((item, index) => (
                        <RoadmapTimelineItem
                            key={item.permaLink}
                            ref={(el) => {
                                if (el) timelineItemRefs.current.set(item.permaLink, el)
                                else timelineItemRefs.current.delete(item.permaLink)
                            }}
                            item={item}
                            isLast={index === items.length - 1}
                            isActive={activeItemId === item.permaLink}
                            onClick={() => handleTimelineClick(item.permaLink)}
                        />
                    ))}
                </Timeline>
            </Box>
        </Box>
    )
}

enum RoadmapFilterType {
    BUGFIX = RoadmapImplementationType.BUGFIX,
    FEATURE = RoadmapImplementationType.FEATURE,
    UPCOMING = RoadmapItemType.UPCOMING,
}

const filterTypeTranslationKeys: Record<RoadmapFilterType, TranslationKey> = {
    [RoadmapFilterType.BUGFIX]: "news-roadmap-type-filter-bugfix",
    [RoadmapFilterType.FEATURE]: "news-roadmap-type-filter-feature",
    [RoadmapFilterType.UPCOMING]: "news-roadmap-type-filter-upcoming",
}

type Props = Readonly<{
    items: RoadmapItem[]
    isPublic?: boolean
}>

function sortByDate(items: RoadmapItem[]): RoadmapItem[] {
    const getDateSource = (item: RoadmapItem) =>
        item.roadmapDetails.itemType === RoadmapItemType.IMPLEMENTED
            ? item.roadmapDetails.implementationDate
            : item.publishDate
    return [...items].sort((a, b) => getDateSource(b).localeCompare(getDateSource(a)))
}

export function RoadmapContent({ items, isPublic }: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const baseItems = useMemo(() => {
        if (!isPublic) return items
        return items.filter((item) => item.roadmapDetails.implementationType !== RoadmapImplementationType.BUGFIX)
    }, [items, isPublic])

    const tabs = useMemo(() => {
        const allTab = {
            label: l10n.getString("news-all"),
            component: <RoadmapList items={sortByDate(baseItems)} />,
        }

        const filterTabs = (Object.values(RoadmapFilterType) as RoadmapFilterType[])
            .filter((type) => !isPublic || type !== RoadmapFilterType.BUGFIX)
            .map((type) => {
                const filtered = baseItems.filter((item) => {
                    if (type === RoadmapFilterType.UPCOMING)
                        return item.roadmapDetails.itemType === RoadmapItemType.UPCOMING
                    if (type === RoadmapFilterType.BUGFIX)
                        return (
                            item.roadmapDetails.itemType === RoadmapItemType.IMPLEMENTED &&
                            item.roadmapDetails.implementationType === RoadmapImplementationType.BUGFIX
                        )
                    if (type === RoadmapFilterType.FEATURE)
                        return (
                            item.roadmapDetails.itemType === RoadmapItemType.IMPLEMENTED &&
                            item.roadmapDetails.implementationType === RoadmapImplementationType.FEATURE
                        )
                    return false
                })

                return {
                    label: l10n.getString(filterTypeTranslationKeys[type]),
                    component: <RoadmapList items={sortByDate(filtered)} />,
                }
            })

        return [allTab, ...filterTabs]
    }, [baseItems, isPublic, l10n])

    if (items.length === 0) return <StandardNoResultText />

    return (
        <Box className={classes.root}>
            <StandardTabs tabs={tabs} queryParam={ROADMAP_FILTER_QUERY_PARAM} />
        </Box>
    )
}
